import { useRef, useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { useCombobox } from 'downshift'
import { type FieldPathValue, type FieldValues, type Path, useController, useForm } from 'react-hook-form'

import { useUnique } from '@subaio/components'
import { type CommonSelectProps, useAsyncOptions } from '@subaio/components/src/HookForm'
import { isNonNullable } from '@subaio/utils'

import {
	ButtonClear,
	ColumnLabel,
	ControlContainer,
	FieldWrapper,
	HintContainer,
	InputComponent,
	InputContainer,
	InputError,
	Label,
	MenuIndicator,
	SelectMenu,
} from '../Shared'
import { InlineValue } from './InlineValue'
import { LoadingLabel } from './LoadingLabel'
import { ValueWrapper } from './ValueWrapper'

const defaultValue: any = ''

/**
 * A select component that allows searching for and selecting a single value.
 * Default return value is `undefined`
 * @example
 * ```tsx
 * <Select name='name' label='Select' options={[{ label: 'Option', value: 'option-1' }, ...]} />
 * ```
 */
export const Select = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	isRequired = false,
	maxNumberOfOptionsVisible = 40,
	isReadOnly = false,
	isDisabled = false,
	name,
	label,
	hint,
	placeholder,
	options: providedOptions,
	shouldAutofocus = false,
	isLoading = false,
	isClearable = true,
	validate = {},
	...rest
}: CommonSelectProps<TPath, FieldPathValue<TFieldValues, TPath>>) => {
	// Input settings and setup
	const id = useUnique(name)
	const { getValues } = useForm()
	const {
		field: { value, onChange },
		fieldState: { error },
	} = useController<TFieldValues, any>({
		name,
		disabled: isDisabled,
		rules: {
			validate: (value) => {
				Object.values(validate).forEach((validator) => {
					if (validator) validator(value, getValues())
				})
				if (isRequired && value.length === 0) return 'This field is required'

				return undefined
			},
		},
		defaultValue,
	})
	const inputRef = useRef<HTMLInputElement>(null)
	const [inputValue, setInputValue] = useState<string>('')
	const hasError = Boolean(error)

	// Options may be defined while loading, if the resource is reloading
	const { options, areOptionsLoading, filteredOptions, filteredValues } = useAsyncOptions(
		providedOptions,
		name,
		inputValue,
	)

	// Downshift setup and settings
	const {
		isOpen: isMenuOpen,
		getLabelProps,
		getMenuProps,
		highlightedIndex,
		getItemProps,
		getInputProps,
		reset,
		openMenu: onOpenMenu,
		closeMenu: onCloseMenu,
		selectItem,
	} = useCombobox({
		id,
		items: filteredValues,
		itemToString: () => '',
		onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem),
		onIsOpenChange: ({ isOpen }) => {
			// If the user clears the field and exits the menu, we assume the user wants to reset the field
			if (!isOpen) setInputValue('')
		},
		onInputValueChange: ({ inputValue }) => setInputValue(inputValue ?? ''),
		initialSelectedItem: value,
		defaultSelectedItem: '',
	})
	const selectedOption = options?.find((option) => option.value === value)

	if (options && value && !selectedOption) {
		throw new Error(`Selected value ${value} has no corresponding option for field ${name}`)
	}

	// Helper hooks for better and more reliable access to hover and focus states
	const { hoverProps, isHovered } = useHover({})
	const { isFocusVisible, focusProps, isFocused } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })

	return (
		<FieldWrapper {...rest}>
			<ColumnLabel>
				<Label {...getLabelProps()} htmlFor={id} isDisabled={isDisabled}>
					<span>{label}</span>
				</Label>
				<InputContainer
					{...hoverProps}
					hasError={hasError}
					isReadOnly={isReadOnly}
					isDisabled={isDisabled}
					isHovered={isHovered}
					isFocusVisible={isFocusVisible}
					isFocus={isFocused}
					onClick={(event) => {
						if (event.defaultPrevented || isDisabled || isReadOnly) return undefined
						onOpenMenu()
					}}
				>
					<ValueWrapper>
						<InputComponent
							{...(getInputProps({
								ref: inputRef,
								name,
								placeholder,
								onFocus: onOpenMenu,
								onClick: onOpenMenu,
							}) as Record<string, unknown>)}
							{...focusProps}
							value={inputValue}
							hasError={hasError}
							disabled={isDisabled || isReadOnly}
							id={id}
							autoFocus={shouldAutofocus}
							shouldShrink={(!inputValue && Boolean(selectedOption)) || areOptionsLoading}
							shouldFill={(!inputValue && !selectedOption) || areOptionsLoading}
							onFocus={(event) => {
								event.target.select()
								onOpenMenu()
								if (focusProps.onFocus) focusProps.onFocus(event)
							}}
							onBlur={async (event) => {
								onCloseMenu()
								if (focusProps.onBlur) focusProps.onBlur(event)
							}}
						/>
						{areOptionsLoading ? (
							<LoadingLabel>Loading...</LoadingLabel>
						) : (
							<InlineValue
								isVisible={!inputValue && Boolean(selectedOption)}
								label={selectedOption?.render ? selectedOption.render(selectedOption) : selectedOption?.label}
							/>
						)}
					</ValueWrapper>
					<ControlContainer>
						{isNonNullable(value) && value !== defaultValue && isClearable && !isReadOnly && !isDisabled && (
							<ButtonClear
								onClick={() => {
									reset()
									onChange(defaultValue)
								}}
							/>
						)}
						<MenuIndicator isMenuOpen={isMenuOpen} isLoading={isLoading || areOptionsLoading} hasError={hasError} />
					</ControlContainer>
				</InputContainer>
				<SelectMenu
					{...(getMenuProps({}, { suppressRefError: true }) as Record<string, unknown>)}
					getItemProps={getItemProps}
					highlightedOptionIndex={highlightedIndex}
					isLoading={isLoading || areOptionsLoading}
					isOpen={isMenuOpen}
					maxNumberOfOptionsVisible={maxNumberOfOptionsVisible}
					options={filteredOptions}
					selectedValues={value === undefined ? [] : [value]}
					onSelectOption={(item) => selectItem(item)}
				/>
				{hint && <HintContainer>{hint}</HintContainer>}
				<InputError hasError={hasError} errorMessage={error?.message as string | undefined} />
			</ColumnLabel>
		</FieldWrapper>
	)
}
